/**
 * @file Contenu du menu mobile (drawer) du header public.
 * @module packages.common.layouts.public.components.HeaderMobileMenu
 *
 * Panneau latéral affiché sur mobile : nom de l'établissement, sélecteur
 * de langue, puis les mêmes entrées que la navigation desktop (coups de
 * cœur, tous les bons, catégories, à propos). Chaque clic déclenche
 * l'action puis ferme le menu via `onClose`.
 */
import React from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Box, Stack, Typography, alpha} from '@mui/material';
import Iconify from 'packages/common/components/iconify';
import {FlowLanguageSelector} from 'packages/common/components/flow-language-selector';

const HeaderMobileMenu = ({companyName, categories = [], hasFeatured, primaryColor, getTranslatedName, availableLanguages, currentLanguage, onChangeLang, onFeaturedClick, onAllGiftsClick, onCategorySelect, onAboutClick, onClose}) => {
  const {t} = useTranslation();

  const hasCategories = categories.length > 0;

  // Entrées simples du menu ; les catégories ont leur propre bloc déroulé
  const menuItems = [
    {key: 'featured', icon: 'mdi:heart', label: t('header.menu.featured'), onClick: onFeaturedClick, isVisible: hasFeatured},
    {key: 'allGifts', icon: 'mdi:gift', label: t('header.menu.allGifts'), onClick: onAllGiftsClick, isVisible: true},
  ];

  const renderMenuItem = ({key, icon, label, onClick}) => (
    <Box
      key={key}
      onClick={() => {
        onClick();
        onClose();
      }}
      sx={{
        'cursor': 'pointer',
        'p': 2,
        'borderRadius': 2,
        '&:hover': {
          bgcolor: alpha(primaryColor, 0.1),
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Iconify icon={icon} width={20} color={primaryColor} />
        <Typography variant="body1" color="text.primary">
          {label}
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        width: 280,
        backgroundColor: 'background.paper',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* En-tête du menu : nom de l'établissement + langue */}
      <Box sx={{
        p: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: alpha(primaryColor, 0.05),
      }}>
        <Typography variant="h6" color="text.primary" fontWeight="bold">
          {companyName}
        </Typography>

        {availableLanguages.length > 1 && (
          <Box sx={{mt: 2}}>
            <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block'}}>
              {t('header.language', 'Langue')}
            </Typography>
            <FlowLanguageSelector
              currentLanguage={currentLanguage}
              availableLanguages={availableLanguages}
              onLanguageChange={onChangeLang}
              fullWidth
            />
          </Box>
        )}
      </Box>

      {/* Entrées de navigation */}
      <Box sx={{flex: 1, p: 2}}>
        <Stack spacing={3}>
          {menuItems.filter((item) => item.isVisible).map(renderMenuItem)}

          {hasCategories && (
            <Box>
              <Box sx={{p: 2, mb: 1}}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Iconify icon="mdi:tag-multiple" width={20} color={primaryColor} />
                  <Typography variant="body1" color="text.primary" fontWeight="500">
                    {t('header.menu.categories')}
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{pl: 2}}>
                {categories.map((category) => (
                  <Box
                    key={category._id}
                    onClick={() => {
                      onCategorySelect(category._id);
                      onClose();
                    }}
                    sx={{
                      'cursor': 'pointer',
                      'p': 1.5,
                      'ml': 2,
                      'borderRadius': 1,
                      '&:hover': {
                        bgcolor: alpha(primaryColor, 0.05),
                      },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {getTranslatedName(category)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {renderMenuItem({key: 'about', icon: 'mdi:information', label: t('header.menu.about'), onClick: onAboutClick})}
        </Stack>
      </Box>
    </Box>
  );
};

HeaderMobileMenu.propTypes = {
  companyName: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
  })),
  hasFeatured: PropTypes.bool.isRequired,
  primaryColor: PropTypes.string.isRequired,
  getTranslatedName: PropTypes.func.isRequired,
  availableLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentLanguage: PropTypes.string.isRequired,
  onChangeLang: PropTypes.func.isRequired,
  onFeaturedClick: PropTypes.func.isRequired,
  onAllGiftsClick: PropTypes.func.isRequired,
  onCategorySelect: PropTypes.func.isRequired,
  onAboutClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default HeaderMobileMenu;
